import { useEffect, useState } from 'react'
import { useCreatorStore } from '../stores/creatorStore'
import { track } from '../lib/analytics'
import CollectionCard from '../components/CollectionCard'

export default function CreatorStudio() {
  const { collections, loading, error, fetchCollections, createCollection } = useCreatorStore()
  const [showModal, setShowModal] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    track('creator_studio_visited')
    fetchCollections()
  }, [fetchCollections])

  const handleCreate = async () => {
    const trimmed = newTitle.trim()
    if (!trimmed || creating) return
    setCreating(true)
    try {
      await createCollection(trimmed)
      setNewTitle('')
      setShowModal(false)
    } catch {
      // keep modal open so user can retry
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <h1 className="text-xl font-bold">Creator Studio</h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
        {loading && collections.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <p className="text-error text-sm mb-4">{error}</p>
            <button
              onClick={() => fetchCollections()}
              className="px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
            >
              Try again
            </button>
          </div>
        ) : collections.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <svg className="w-16 h-16 text-text-muted opacity-30 mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
            </svg>
            <h2 className="text-xl font-semibold mb-2">No collections yet</h2>
            <p className="text-text-secondary mb-6">
              Create a collection to gather research and generate content.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-accent text-bg px-6 py-3 rounded-lg font-medium hover:bg-accent-hover transition-colors"
            >
              New Collection
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {collections.map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        )}
      </div>

      {/* FAB */}
      {collections.length > 0 && (
        <button
          onClick={() => setShowModal(true)}
          aria-label="New collection"
          className="absolute bottom-20 right-4 w-14 h-14 bg-accent text-bg rounded-full shadow-lg flex items-center justify-center hover:bg-accent-hover transition-colors z-10"
        >
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      )}

      {/* New Collection Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
          onKeyDown={(e) => e.key === 'Escape' && setShowModal(false)}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false) }}
        >
          <div className="bg-surface w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
            <h2 className="text-lg font-semibold">New Collection</h2>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowModal(false) }}
              placeholder="Collection title..."
              autoFocus
              className="w-full bg-elevated text-text-primary placeholder-text-muted rounded-lg px-4 py-3 outline-none focus:ring-1 focus:ring-accent"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowModal(false); setNewTitle('') }}
                className="flex-1 py-3 rounded-lg bg-elevated text-text-secondary font-medium hover:bg-bg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newTitle.trim() || creating}
                className="flex-1 py-3 rounded-lg bg-accent text-bg font-medium hover:bg-accent-hover transition-colors disabled:opacity-30"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
